import { useCallback, useState, useEffect, useMemo } from "react";
import { getTenantIdFromToken } from "@/utils/auth";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import {
  TenantSearchFilterContext,
  TenantSearchFilterContextValue,
} from "./hooks/useTenantSearchFilterState";
import useTenantSearchFilterStore from "./stores/tenant-search-filter.store";

interface TenantSearchFilterProviderProps {
  children: React.ReactNode;
}

export const TenantSearchFilterProvider = ({
  children,
}: TenantSearchFilterProviderProps) => {
  const getCachedToken = useCachedAccessToken();
  const markInitialized = useTenantSearchFilterStore(
    (state) => state.markInitialized
  );
  const initializedTenants = useTenantSearchFilterStore(
    (state) => state.initializedTenants
  );
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const id = await getTenantIdFromToken(getCachedToken);
        setTenantId(id || null);
      } catch {
        setTenantId(null);
      }
    };
    fetchTenantId();
  }, [getCachedToken]);

  const initializeDefaults = useCallback(
    (
      filters: Record<string, string[]>,
      sources: string[],
      defaultsPlan: {
        sourceKey: string;
        childFilters: { id: string; value: unknown }[];
      } | null
    ): { filters: Record<string, string[]>; sources: string[] } => {
      if (!defaultsPlan) {
        return { filters, sources };
      }

      if (typeof window !== "undefined") {
        const sessionData = sessionStorage.getItem("search-store");
        if (sessionData) {
          return { filters, sources };
        }
      }

      if (sources.length > 0 || Object.keys(filters).length > 0) {
        return { filters, sources };
      }

      const newSources = [defaultsPlan.sourceKey];
      const newFilters: Record<string, string[]> = {};

      for (const cf of defaultsPlan.childFilters || []) {
        const val = cf.value;
        const arr = Array.isArray(val)
          ? val.map(String)
          : val != null
            ? [String(val)]
            : [];
        newFilters[cf.id] = arr;
      }

      return { filters: newFilters, sources: newSources };
    },
    []
  );

  const markDefaultsInitialized = useCallback(() => {
    if (tenantId) {
      markInitialized(tenantId);
    }
  }, [tenantId, markInitialized]);

  // Make hasInitializedDefaults reactive by subscribing to store changes
  const hasInitializedDefaults = useMemo(() => {
    return tenantId ? initializedTenants.includes(tenantId) : false;
  }, [tenantId, initializedTenants]);

  const value: TenantSearchFilterContextValue = {
    hasInitializedDefaults,
    initializeDefaults,
    markDefaultsInitialized,
  };

  return (
    <TenantSearchFilterContext.Provider value={value}>
      {children}
    </TenantSearchFilterContext.Provider>
  );
};
